import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';
import { marked, Renderer } from 'marked';
import DOMPurify from 'dompurify';
import '../assets/styles/Project.scss';

interface WriteupEntry {
  file: string;
  slug: string;
  title: string;
  mtime: string;
  difficulty?: string;
  tags?: string[];
}

export default function WriteupPage() {
  const { id } = useParams<{ id: string }>();
  const [content, setContent] = useState<string | null>(null);
  const [title, setTitle] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!id) return;
    const currentSlug = decodeURIComponent(id);

    async function load() {
      try {
        const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');

        // 1. Load index.json
        const manifestUrl = `${base}/writeups/index.json`;
        const manifestRes = await fetch(manifestUrl);
        if (!manifestRes.ok) throw new Error(`Could not load manifest from ${manifestUrl}`);

        const list: WriteupEntry[] = await manifestRes.json();

        // 2. Find the writeup by slug
        const entry = list.find((it) => it.slug === currentSlug);
        if (!entry) throw new Error(`Writeup not found: ${currentSlug}`);

        // 3. Load the Markdown file
        const cleanFile = entry.file.startsWith('/') ? entry.file.substring(1) : entry.file;
        const mdUrl = `${base}/writeups/${cleanFile}`;

        const mdRes = await fetch(mdUrl);
        if (!mdRes.ok) throw new Error(`Could not load markdown from ${mdUrl}`);
        
        let md = await mdRes.text();

        // --- Processamento do Markdown ---

        // Remover Frontmatter
        md = md.replace(/^---\s*\r?\n[\s\S]*?\r?\n---\s*/, '');

        // Extrair Título H1
        const h1Match = md.match(/^#\s+(.+)$/m);
        if (h1Match) {
          setTitle(h1Match[1].trim());
          md = md.replace(/^#\s+.+$/m, '');
        } else {
          setTitle(entry.title);
        }

        // Corrigir caminhos de imagens
        const dirname = entry.file.includes('/') 
          ? entry.file.substring(0, entry.file.lastIndexOf('/')) 
          : '';
        
        const basePath = `${base}/writeups/${dirname}/`;

        // Fix Markdown images
        md = md.replace(/!\[([^\]]*)\]\((?!https?:|\/)([^)]+)\)/g, (m, alt, url) => {
          return `![${alt}](${basePath}${url})`;
        });

        // Fix HTML images (removendo escapes desnecessários para o ESLint)
        md = md.replace(/<img([^>]+)src="([^"]+)"/g, (m, before, url) => {
          if (url.startsWith('http') || url.startsWith('/')) return m;
          return `<img${before}src="${basePath}${url}"`;
        });

        setContent(md.trim());
      } catch (e: any) {
        console.error("Fetch Error:", e);
        setError(e.message || 'Failed to load');
      }
    }

    load();
  }, [id]);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el || !content) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG') {
        const img = target as HTMLImageElement;
        setLightbox({ src: img.src, alt: img.alt });
      }
    };
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
  }, [content]);

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox]);

  useEffect(() => () => {
    if (copyTimer.current) clearTimeout(copyTimer.current);
  }, []);

  /** The page URL is already the canonical permalink, so copy it as-is. */
  async function copyLink() {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard API needs a secure context; fall back to a hidden textarea.
      const ta = document.createElement('textarea');
      ta.value = url;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* nothing else to try */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(false), 1600);
  }

  const escapeHtml = (value: string) => value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const highlightCodeLine = (line: string) => {
    const escaped = escapeHtml(line);
    const stringTokens: string[] = [];

    const redactedAwsKeys = escaped.replace(/AKIA[0-9A-Z]{16}/g, '<span class="code-aws-prefix">AKIA</span><span class="code-redacted">&lt;REDACTED&gt;</span>');

    const withStringsMasked = redactedAwsKeys.replace(/(?:"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*')/g, (match) => {
      const token = `__STRING_${stringTokens.length}__`;
      stringTokens.push(`<span class="code-string">${match}</span>`);
      return token;
    });

    const commentIndex = withStringsMasked.indexOf('#');
    const codePart = commentIndex >= 0 ? withStringsMasked.slice(0, commentIndex) : withStringsMasked;
    const commentPart = commentIndex >= 0 ? withStringsMasked.slice(commentIndex) : '';

    const processedCode = codePart
      .replace(/^(\s*)(def\s+)([A-Za-z_][\w]*)/m, '$1<span class="code-keyword">def</span> <span class="code-function-name">$3</span>')
      .replace(/^(\s*)(async\s+function\s+)([A-Za-z_][\w]*)/m, '$1<span class="code-keyword">async function</span> <span class="code-function-name">$3</span>')
      .replace(/^(\s*)(function\s+)([A-Za-z_][\w]*)/m, '$1<span class="code-keyword">function</span> <span class="code-function-name">$3</span>')
      .replace(/^(\s*)(for|while)\b/g, '$1<span class="code-loop">$2</span>')
      .replace(/\b(for|while)\b/g, '<span class="code-loop">$1</span>')
      .replace(/^(\s*)([A-Za-z_][\w]*)\s*=(?!=)/m, '$1<span class="code-variable">$2</span> =')
      .replace(/\b([A-Za-z_][\w]*)\s*=(?!=)/g, '<span class="code-variable">$1</span> =')
      .replace(/\b([A-Za-z_][\w]*)\s+in\b/g, '<span class="code-loop-variable">$1</span> <span class="code-keyword">in</span>')
      .replace(/\b\d+(?:\.\d+)?\b/g, '<span class="code-number">$&</span>');

    const restoredStrings = processedCode.replace(/__STRING_(\d+)__/g, (_, index) => stringTokens[Number(index)] || '');
    return restoredStrings + (commentPart ? `<span class="code-comment">${commentPart}</span>` : '');
  };

  const highlightCodeBlock = (value: string) => value
    .split('\n')
    .map((line) => highlightCodeLine(line))
    .join('\n');

  const highlightFunctions = (value: string) => highlightCodeBlock(value);

  const renderer = new Renderer();
  renderer.code = (code, infostring) => {
    const lang = (infostring || '').trim();
    return `<pre><code${lang ? ` class="language-${lang}"` : ''}>${highlightFunctions(code)}</code></pre>`;
  };

  const renderMarkdown = (md: string) => {
    // Usando marked.parse para versões mais recentes da lib
    const html = marked.parse(md, { renderer });
    const sanitized = DOMPurify.sanitize(html as string).replace(
      /AKIA[0-9A-Z]{16}/g,
      '<span class="code-aws-prefix">AKIA</span><span class="code-redacted">&lt;REDACTED&gt;</span>'
    );
    return { __html: sanitized };
  };

  return (
    <>
    <div className="projects-container" style={{ paddingTop: 96 }}>
      <div className="writeup-page-shell" style={{ maxWidth: 980, width: '100%', padding: '0 5%', margin: '0 auto' }}>
        <nav style={{ marginBottom: '2rem' }}>
          <Link to="/writeups" className="back-link">
            &larr; Back to Writeups
          </Link>
        </nav>

        {error ? (
            <div className="error-container" style={{ textAlign: 'left', marginTop: '50px' }}>
            <h2 style={{ color: 'var(--principal)' }}>Error</h2>
            <p style={{ color: 'white', marginBottom: '20px' }}>{error}</p>
            <Link to="/writeups" style={{ color: 'var(--principal)', textDecoration: 'underline' }}>
              Return to list
            </Link>
            </div>
        ) : content ? (
          <article>
            <header style={{ marginBottom: '2rem' }}>
              <div className="writeup-title-row">
                <h1 style={{ fontSize: '2.5rem', margin: 0, color: 'white' }}>{title}</h1>
                <button
                  type="button"
                  className={`writeup-copy-link${copied ? ' writeup-copy-link--copied' : ''}`}
                  onClick={copyLink}
                  aria-label={copied ? 'Link copied' : 'Copy link to this writeup'}
                  title={copied ? 'Link copied' : 'Copy link'}
                >
                  {copied ? <CheckIcon /> : <LinkIcon />}
                </button>
              </div>
            </header>
            <div
              ref={bodyRef}
              className="writeup-body"
              dangerouslySetInnerHTML={renderMarkdown(content)}
            />
          </article>
        ) : (
          <div className="loading" style={{ textAlign: 'left', color: 'white' }}>
            <p>Loading writeup...</p>
          </div>
        )}
      </div>
    </div>
    {lightbox && (
      <div
        onClick={() => setLightbox(null)}
        style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'zoom-out',
        }}
      >
        <img
          src={lightbox.src}
          alt={lightbox.alt}
          style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, boxShadow: '0 8px 40px rgba(0,0,0,0.6)' }}
          onClick={(e) => e.stopPropagation()}
        />
        <button
          onClick={() => setLightbox(null)}
          style={{
            position: 'absolute', top: 20, right: 28,
            background: 'none', border: 'none',
            color: 'white', fontSize: '2rem', cursor: 'pointer', lineHeight: 1,
          }}
          aria-label="Close"
        >
          &times;
        </button>
      </div>
    )}
    </>
  );
}